import { SxProps, Theme } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { GapBox } from 'components/Box'
import { BoxForm, RadioGroup, Switch, TextField } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { TooltipProps } from 'components/Tooltip'
import { PAGE_ARCHITECTURE_KNOWLEDGE_BASE_URL } from 'features/inventory/page/constants'
import { useAbility } from 'providers/casl'
import { CONTAINER_SPACING } from 'providers/material-ui/theme/constants'
import { useMatchTypeRadioGroup } from '../../hooks/useMatchTypeRadioGroup'
import { CommonSchema } from '../../schemas/commonSchema'
import { CommonFormProps } from '../../types'

export const TriggersSection = ({ variant }: CommonFormProps) => {
  const { t } = useTranslation(['features/page', 'common'])
  const { control, watch } = useFormContext<CommonSchema>()
  const ability = useAbility()

  const matchTypeTooltip: TooltipProps = {
    content: [
      t('form.general.triggers.matchType.tooltip.line1'),
      t('form.general.triggers.matchType.tooltip.line2'),
      t('form.general.triggers.matchType.tooltip.line3'),
      t('form.general.triggers.matchType.tooltip.line4'),
    ],
    link: {
      title: t('form.general.triggers.matchType.tooltip.link'),
      url: PAGE_ARCHITECTURE_KNOWLEDGE_BASE_URL,
    },
  }

  const matchTypeOptions = useMatchTypeRadioGroup()

  const isDisabled: boolean =
    variant === 'add' ? ability.cannot('create', 'Page') : ability.cannot('update', 'Page')

  const sx: SxProps<Theme> = {
    ml: CONTAINER_SPACING,
  }

  const { customVariableTriggerEnabled, pathTriggerEnabled } = watch()

  return (
    <Paper>
      <SectionHeader
        subtitle={t('form.general.triggers.subtitle')}
        title={t('form.general.triggers.title')}
      />

      <GapBox />

      <BoxForm>
        <Switch
          control={control}
          disabled={isDisabled}
          label={t('form.general.triggers.pathTriggerEnabled.label')}
          labelPlacement='start'
          name='pathTriggerEnabled'
          tooltip={{
            content: t('form.general.triggers.pathTriggerEnabled.tooltip'),
          }}
        />

        {pathTriggerEnabled && (
          <BoxForm sx={sx}>
            <TextField
              control={control}
              disabled={isDisabled}
              label={t('form.general.triggers.path.label')}
              name='path'
              placeholder={t('form.general.triggers.path.placeholder')}
              tooltip={{
                content: t('form.general.triggers.path.tooltip'),
                link: {
                  title: t('form.general.triggers.matchType.tooltip.link'),
                  url: PAGE_ARCHITECTURE_KNOWLEDGE_BASE_URL,
                },
              }}
            />

            <RadioGroup
              control={control}
              disabled={isDisabled}
              label={t('form.general.triggers.matchType.name')}
              name='matchType'
              options={matchTypeOptions}
              tooltip={matchTypeTooltip}
            />
          </BoxForm>
        )}
      </BoxForm>

      <GapBox />

      <BoxForm>
        <Switch
          control={control}
          disabled={isDisabled}
          label={t('form.general.triggers.customVariableTriggerEnabled.label')}
          labelPlacement='start'
          name='customVariableTriggerEnabled'
          tooltip={{
            content: t('form.general.triggers.customVariableTriggerEnabled.tooltip'),
          }}
        />

        {customVariableTriggerEnabled && (
          <BoxForm sx={sx}>
            <TextField
              control={control}
              disabled={isDisabled}
              label={t('form.general.triggers.customVariableName.label')}
              name='customVariableName'
              placeholder={t('form.general.triggers.customVariableName.placeholder')}
            />

            <TextField
              control={control}
              disabled={isDisabled}
              label={t('form.general.triggers.customVariableValue.label')}
              name='customVariableValue'
              placeholder={t('form.general.triggers.customVariableValue.placeholder')}
            />
          </BoxForm>
        )}
      </BoxForm>
    </Paper>
  )
}
